// @docs:
//   - docs/core/architecture/PASSAGEWIKI_SITE.md
// ==============================================
// Admin — PassageWiki reports (moderation queue for public passages)
// ==============================================
// Anyone signed in to passagewiki.com can report a public passage (hazard /
// inaccurate / out of date / copyright / inappropriate / other). Admins resolve
// or dismiss, optionally withdrawing the passage from the commons. Nothing here
// deletes content: the owner keeps their passage; it just stops being public.
// Works exactly like the shared-library queue next to it.
// Readability mandate: nothing under 14px, primary copy 16px+.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FlagIcon, EyeSlashIcon, GlobeAltIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import type { PassageReport, PassageReportStatus } from '../../types/passageReports';
import { PASSAGE_REPORT_REASON_LABEL } from '../../types/passageReports';

// PassageWiki is served under a path on the MyPolaris host for now; when it moves
// to passagewiki.com, set VITE_PASSAGEWIKI_URL (or change this default).
const PASSAGEWIKI_URL = ((import.meta.env.VITE_PASSAGEWIKI_URL as string | undefined) ?? 'https://passagewiki.com').replace(/\/$/, '');
export const publicPassageUrl = (elementId: string) => `${PASSAGEWIKI_URL}/p/${elementId}`;

const STATUS_CLS: Record<PassageReportStatus, string> = {
  open: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  resolved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  dismissed: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
};
const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export function AdminPassageReportsPage() {
  const qc = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const [status, setStatus] = useState<PassageReportStatus>('open');
  const [active, setActive] = useState<PassageReport | null>(null);
  const [note, setNote] = useState('');
  const [unpublish, setUnpublish] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['admin-passage-reports', status], queryFn: () => api.adminPassageReports(status) });

  const resolve = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'resolved' | 'dismissed' }) =>
      api.adminPassageResolveReport(id, { status: decision, note: note.trim() || null, unpublish }),
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ['admin-passage-reports'] });
      setActive(null); setNote(''); setUnpublish(false);
      addToast({ type: 'success', title: v.decision === 'resolved' ? 'Report resolved' : 'Report dismissed', message: unpublish ? 'The passage has been withdrawn from PassageWiki.' : undefined });
    },
    onError: () => addToast({ type: 'error', title: 'Could not update the report' }),
  });

  const rows = data ?? [];
  const openReview = (r: PassageReport) => { setActive(r); setNote(''); setUnpublish(false); };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-white flex items-center gap-2"><FlagIcon className="w-6 h-6 text-amber-400" /> PassageWiki reports</h1>
        <span className="flex-1" />
        <div className="inline-flex bg-slate-800 border border-slate-600 rounded-lg p-0.5" role="radiogroup" aria-label="Report status">
          {(['open', 'resolved', 'dismissed'] as const).map((s) => (
            <button key={s} type="button" role="radio" aria-checked={status === s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold capitalize ${status === s ? 'bg-amber-400 text-slate-900' : 'text-slate-400 hover:text-white'}`}>{s}</button>
          ))}
        </div>
      </div>
      <p className="text-base text-slate-300">
        Reports from PassageWiki users about passages published to the commons. Resolving or dismissing records your decision;
        “withdraw from PassageWiki” takes the passage off the public site without deleting anything — the owner keeps it and can see it is no longer public.
      </p>

      <div className="overflow-x-auto rounded-2xl border border-slate-700 bg-slate-800/60">
        <table className="w-full text-base">
          <thead>
            <tr className="text-left text-sm uppercase tracking-wide text-slate-400 border-b border-slate-700">
              <th className="px-4 py-3">Passage</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Reported by</th><th className="px-4 py-3">When</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/70">
            {isLoading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No {status} reports.</td></tr>
              : rows.map((r) => {
                const gone = r.elementName === null;
                return (
                  <tr key={r.id} className="align-top">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{r.elementName ?? '(deleted passage)'}</div>
                      <div className="text-sm text-slate-400 flex flex-wrap items-center gap-2">
                        <span>{r.elementType ?? 'passage'} · by {r.ownerName ?? '—'}</span>
                        {!gone && (r.elementIsPublic
                          ? <span className="inline-flex items-center gap-1 text-sm text-emerald-300 border border-emerald-500/40 rounded-full px-2"><GlobeAltIcon className="w-3.5 h-3.5" /> public</span>
                          : <span className="inline-flex items-center gap-1 text-sm text-slate-300 border border-slate-600 rounded-full px-2"><EyeSlashIcon className="w-3.5 h-3.5" /> withdrawn</span>)}
                      </div>
                      {!gone && r.elementIsPublic && (
                        <a href={publicPassageUrl(r.elementId)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-amber-300 hover:underline mt-1">
                          Open on PassageWiki <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{PASSAGE_REPORT_REASON_LABEL[r.reason] ?? r.reason}</div>
                      {r.details && <div className="text-sm text-slate-300 max-w-md whitespace-pre-line">“{r.details}”</div>}
                      {r.resolutionNote && <div className="text-sm text-slate-400 mt-1">Admin: {r.resolutionNote}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300"><div>{r.reporterName ?? '—'}</div><div className="text-slate-500">{r.reporterEmail}</div></td>
                    <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">{fmt(r.createdAt)}{r.resolvedAt && <div className="text-slate-500">closed {fmt(r.resolvedAt)}</div>}</td>
                    <td className="px-4 py-3"><span className={`inline-flex rounded-full border px-2 py-0.5 text-sm font-semibold ${STATUS_CLS[r.status]}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === 'open' && <button type="button" onClick={() => openReview(r)} className="px-3 py-1.5 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold hover:brightness-105">Review</button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="passage-report-review-title">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-lg space-y-4">
            <h3 id="passage-report-review-title" className="text-lg font-bold text-white">Review report — {active.elementName ?? '(deleted passage)'}</h3>
            <p className="text-base text-slate-300">
              <b>{PASSAGE_REPORT_REASON_LABEL[active.reason] ?? active.reason}</b>{active.details ? ` — “${active.details}”` : ''}<br />
              <span className="text-sm text-slate-400">reported by {active.reporterName ?? active.reporterEmail ?? 'a PassageWiki user'}</span>
            </p>
            {active.reason === 'hazard' && (
              <p className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                A hazard report. If you cannot verify it quickly, withdrawing the passage while you check is the cautious choice — the owner loses nothing.
              </p>
            )}
            <label className="block text-sm font-semibold text-slate-200">Note (kept with the report)
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 text-white text-base px-3 py-2" placeholder="What you checked and decided" />
            </label>
            <label className={`flex items-start gap-3 bg-slate-900/60 border border-slate-700 rounded-xl p-3 ${active.elementIsPublic ? 'cursor-pointer' : 'opacity-60'}`}>
              <input type="checkbox" checked={unpublish} disabled={!active.elementIsPublic} onChange={(e) => setUnpublish(e.target.checked)} className="mt-1 w-5 h-5 rounded bg-slate-700 border-slate-600 text-amber-400 focus:ring-amber-400" />
              <span>
                <span className="block text-sm font-semibold text-white">Withdraw the passage from PassageWiki</span>
                <span className="block text-sm text-slate-400">{active.elementIsPublic ? 'It leaves the public site, Explore and the next quarterly dump. The owner keeps it; nothing is deleted.' : 'Already withdrawn — it is no longer public.'}</span>
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setActive(null)} className="px-4 py-2 rounded-lg border border-slate-600 text-white text-base">Cancel</button>
              <button type="button" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: active.id, decision: 'dismissed' })} className="px-4 py-2 rounded-lg border border-slate-600 text-white text-base font-semibold hover:border-amber-400">Dismiss</button>
              <button type="button" disabled={resolve.isPending} onClick={() => resolve.mutate({ id: active.id, decision: 'resolved' })} className="px-4 py-2 rounded-lg bg-amber-400 text-slate-900 text-base font-bold hover:brightness-105">{resolve.isPending ? 'Saving…' : 'Resolve'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
